import api from '../utils/api';

class QuotaService {
  constructor() {
    this.baseURL = '/subscriptions'; // Backend endpoint: /api/v1/subscriptions/{userId}/quota
  }

  /**
   * Get storage quota information for a user
   * @param {number} userId - The user ID
   * @returns {Promise<Object>} Quota information including limit, used, remaining, and overLimit info
   */
  async getUserQuota(userId) {
    try {
      const response = await api.get(`${this.baseURL}/${userId}/quota`);
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  /**
   * Format storage seconds into human-readable format
   * @param {number} seconds - Storage in seconds
   * @returns {string} Formatted storage string
   */
  formatStorage(seconds) {
    if (!seconds || seconds === 0) return '0 hours';
    
    const hours = Math.round(seconds / 3600);
    
    // For large storage amounts, show hours instead of days for clarity
    if (hours >= 24) {
      return `${hours} hours`;
    }
    
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }

  /**
   * Format purchase credits into human-readable format
   * @param {number} credits - Number of credits
   * @returns {string} Formatted credits string
   */
  formatCredits(credits) {
    if (!credits || credits === 0) return '0 credits';
    return `${credits} credit${credits > 1 ? 's' : ''}`;
  }

  /**
   * Calculate storage usage percentage
   * @param {number} used - Used storage in seconds
   * @param {number} limit - Storage limit in seconds
   * @returns {number} Usage percentage (0-100)
   */
  calculateUsagePercentage(used, limit) {
    if (!limit || limit === 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  }

  /**
   * Get storage status (safe, warning, critical)
   * @param {number} used - Used storage in seconds
   * @param {number} limit - Storage limit in seconds
   * @returns {string} Status: 'safe', 'warning', 'critical'
   */
  getStorageStatus(used, limit) {
    if (!limit || limit === 0) return 'unlimited';
    
    const percentage = this.calculateUsagePercentage(used, limit);
    
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning';
    return 'safe';
  }

  /**
   * Check if user is over storage limit
   * @param {number} used - Used storage in seconds
   * @param {number} limit - Storage limit in seconds
   * @returns {boolean} True if over limit
   */
  isOverLimit(used, limit) {
    if (!limit || limit === 0) return false;
    return used > limit;
  }

  /**
   * Calculate purchase credit usage percentage
   * @param {number} used - Used credits
   * @param {number} limit - Credit limit
   * @returns {number} Usage percentage (0-100)
   */
  calculateCreditUsagePercentage(used, limit) {
    if (!limit || limit === 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  }

  /**
   * Get purchase credit status (safe, warning, critical)
   * @param {number} used - Used credits
   * @param {number} limit - Credit limit
   * @returns {string} Status: 'safe', 'warning', 'critical'
   */
  getCreditStatus(used, limit) {
    if (!limit || limit === 0) return 'unlimited';
    
    const percentage = this.calculateCreditUsagePercentage(used, limit);
    
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning';
    return 'safe';
  }

  /**
   * Check if user is over credit limit
   * @param {number} used - Used credits
   * @param {number} limit - Credit limit
   * @returns {boolean} True if over limit
   */
  isOverCreditLimit(used, limit) {
    if (!limit || limit === 0) return false;
    return used > limit;
  }

  /**
   * Extract error message from API response
   * @param {Error} error - The error object
   * @returns {string} Error message
   */
  _extractErrorMessage(error) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.message) {
      return error.message;
    }
    return 'An error occurred while fetching quota information';
  }
}

const quotaService = new QuotaService();
export default quotaService;
